/**
 * 通用模糊查询数据类型枚举
 * 支持的所有数据类型及其别名
 *
 * 基于后端策略模式实现，支持租户数据和平台数据的区分查询
 */
export enum UniversalSearchDataType {
  /** 海港数据 (别名: sea) */
  SEAPORT = 'seaport',
  /** 承运人数据 */
  CARRIER = 'carrier',
  /** 航线数据，当前后端 route 策略已查询新版航线数据 */
  ROUTE = 'route',
  /** 机场数据 (别名: air) */
  AIRPORT = 'airport',
  /** 码头数据 */
  TERMINAL = 'terminal',
  /** 国家数据 */
  COUNTRY = 'country',
  /** 国际行政区划 */
  INTERNATIONAL_DIVISION = 'international_division',
  /** 行政区划 (别名: china_division, division) */
  ADMINISTRATIVE_DIVISION = 'administrative_division',
  /** 海外仓库 (别名: warehouse) */
  OVERSEAS_WAREHOUSE = 'overseas_warehouse',
  /** 邮编数据 (别名: china_postal, postal_cn, postal_us, postal_code) */
  POSTAL_CODE = 'postal_code',
  /** 费用管理 (别名: fee) */
  FEE = 'fee',
  /** 汇率设置 */
  EXCHANGE_RATE = 'exchange_rate',
  /** 币种数据 */
  CURRENCY = 'currency',
  /** 分公司 */
  OFFICE = 'office',
  /** 合约 */
  CONTRACT = 'contract',
  /** 计费单位 */
  BILLING_UNIT = 'billing_unit',
  /** 船舶代理 (别名: ship_agent, agent) */
  SHIPPING_AGENT = 'shipping_agent',
  /** 船泊资料 */
  SHIP = 'ship_data',
  /** 用户数据 */
  USER = 'user',
  /** 企业数据 */
  ENTERPRISE = 'enterprise',
  /** 企业联系人 */
  ENTERPRISE_CONTACT = 'enterprise_contact',
  /** 模板 */
  TEMPLATE = 'template',
  /** 模板类型 */
  TEMPLATE_TYPE = 'template_type',
  /** 包装单位 */
  PACKING_UNIT = 'packing_unit',
  /** 运输条款 */
  TRANSPORT_TERM = 'transport_term',
  /** 租户 */
  TENANT = 'tenant',
  /** 贸易条款 */
  TRADE_TERM = 'trade_term',
  /** 箱型 */
  CONTAINER_TYPE = 'container_type',
  /** 铁路站点 */
  RAILWAY_STATION = 'railway_station',
  /** 密度等级 */
  DENSITY_DEGREE = 'density_degree',
  /** 货物类型 */
  CARGO_TYPE = 'cargo_type',
  /** 港口组 */
  PORT_GROUP = 'port_group'
}

/**
 * 数据类型功能支持映射
 */
export const DATA_TYPE_FEATURES = {
  [UniversalSearchDataType.COUNTRY]: {
    supportsTenant: true,       // 支持租户数据
    supportsPlatform: true,     // 支持平台数据
    supportsHierarchy: false,   // 不支持层级查询
    defaultEnabled: 1
  },
  [UniversalSearchDataType.ADMINISTRATIVE_DIVISION]: {
    supportsTenant: true,
    supportsPlatform: true,
    supportsHierarchy: true,    // 支持层级查询
    supportsParentId: true,     // 支持parentId参数
    supportsPostalCode: true,   // 支持邮编查询
    defaultEnabled: 1
  },
  [UniversalSearchDataType.INTERNATIONAL_DIVISION]: {
    supportsTenant: false,      // 仅平台数据
    supportsPlatform: true,
    supportsHierarchy: true,
    supportsParentId: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.POSTAL_CODE]: {
    supportsTenant: true,
    supportsPlatform: true,
    supportsPostalCode: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.SEAPORT]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.AIRPORT]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.TERMINAL]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.CARRIER]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.SHIPPING_AGENT]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.ROUTE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.OVERSEAS_WAREHOUSE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.FEE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.CURRENCY]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.OFFICE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.EXCHANGE_RATE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.BILLING_UNIT]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.SHIP]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.USER]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.ENTERPRISE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.TEMPLATE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.TEMPLATE_TYPE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.TRADE_TERM]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.PACKING_UNIT]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.CONTAINER_TYPE]: {
    supportsTenant: true,
    supportsPlatform: true,
    isPlatformData: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.RAILWAY_STATION]: {
    supportsTenant: false,
    supportsPlatform: true,
    isPlatformData: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.DENSITY_DEGREE]: {
    supportsTenant: false,
    supportsPlatform: true,
    isPlatformData: true,
    defaultEnabled: 1
  },
  [UniversalSearchDataType.CARGO_TYPE]: {
    supportsTenant: true,
    supportsPlatform: true,
    defaultEnabled: 1
  }
} as const

/**
 * 始终查询租户的数据类型。
 */
export const NO_PLATFORM_DATA_TYPES: UniversalSearchDataType[] = [
  UniversalSearchDataType.SEAPORT,
  UniversalSearchDataType.AIRPORT,
  UniversalSearchDataType.RAILWAY_STATION,
  UniversalSearchDataType.ROUTE,
  UniversalSearchDataType.FEE,
  UniversalSearchDataType.PORT_GROUP
]

/**
 * 解析 isPlatformData 参数：平台统一维护的数据类型强制查询租户数据。
 */
export function resolvePlatformData(dataType: string, isPlatformData: boolean | undefined): boolean | undefined {
  return NO_PLATFORM_DATA_TYPES.some(t => t.toLowerCase() === dataType.toLowerCase())
    ? false
    : isPlatformData
}
